import i18n from '@/utils/i18n';
import { ChatStatus } from '@/enums';
import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CustomerGroups_ListQuery,
  CustomerGroups_ListQueryVariables,
} from '@/types';
import {
  EmployeeRestaurant,
  useAuthenticatedUserContext,
} from '@/contexts/user-context';
import { gql } from '@apollo/client';
import { Option } from '@/utils/search-filters';
import { PillButtonType } from '@/components/pill-button';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useOfflineDataStatus } from './use-offline-data';
import { useOfflineModeEnabled } from './use-offline-mode-enabled';
import { useWrappedQuery } from './use-wrapped-query';
import { View } from 'react-native';

export const CUSTOMER_GROUPS_LIST_QUERY = gql`
  query CustomerGroups_List($supplierUUID: uuid!) {
    restaurant_groups(
      order_by: { name: asc }
      where: { owner_uuid: { _eq: $supplierUUID } }
    ) {
      name
      restaurant_group_members {
        restaurant_uuid
        uuid
      }
      uuid
    }
  }
`;

export enum SingleSelectFilterType {
  AvailableOffline = 'available_offline',
}

export enum MultiSelectFilterType {
  CustomerGroups = 'customer_groups',
  Status = 'status',
  Supplier = 'supplier',
}

const STATUS_TITLE_MAP = {
  [ChatStatus.ACTIVE]: i18n.t('active'),
  [ChatStatus.NEW]: i18n.t('new'),
  [ChatStatus.PROSPECT]: i18n.t('prospect'),
};

export const useLocationPickerFilterPills = (
  employeeRestaurants: EmployeeRestaurant[],
) => {
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<Set<string>>(
    new Set(),
  );
  const [selectedCustomerGroupsFilter, setSelectedCustomerGroupsFilter] =
    useState<Set<string>>(new Set());
  const [selectedSupplierFilter, setSelectedSupplierFilter] = useState<
    Set<string>
  >(new Set());
  const [accountModeFilter, setAccountModeFilter] =
    useState<SingleSelectFilterType | null>(null);
  const [expandedMultiSelectFilter, setExpandedMultiSelectFilter] =
    useState<MultiSelectFilterType | null>(null);

  const pillButtonRefs = useRef(
    new Map<MultiSelectFilterType, React.RefObject<View | null>>(),
  ).current;

  const getPillRef = useCallback(
    (id: MultiSelectFilterType) => {
      if (!pillButtonRefs.has(id)) {
        pillButtonRefs.set(id, createRef<View>());
      }
      return pillButtonRefs.get(id)!;
    },
    [pillButtonRefs],
  );

  const {
    restaurantRelationships: allRestaurantRelationships,
    selectedRestaurantRelationship,
    supplierEmployeeRole,
  } = useAuthenticatedUserContext();
  const isSupplierEmployee = Boolean(supplierEmployeeRole);

  // Map each supplier to its name across all accounts (not just the filtered
  // set) so these values stay stable even while filtering
  const supplierUUIDToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const relationship of allRestaurantRelationships) {
      map.set(relationship.supplierUUID, relationship.supplierName);
    }
    return map;
  }, [allRestaurantRelationships]);

  const hasMultipleSuppliers = supplierUUIDToNameMap.size > 1;
  const showSupplierFilter = isSupplierEmployee && hasMultipleSuppliers;
  const customerGroupsEnabled = useDynamicConfigValue(
    'account_selector_customer_group_filter_enabled',
  );

  const { data } = useWrappedQuery<
    CustomerGroups_ListQuery,
    CustomerGroups_ListQueryVariables
  >(CUSTOMER_GROUPS_LIST_QUERY, {
    fetchPolicy: 'no-cache',
    skip: !isSupplierEmployee || !customerGroupsEnabled,
    variables: {
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
    },
  });

  const isOfflineModeEnabled = useOfflineModeEnabled();
  const {
    data: dataStatus,
    refetch: refetchDataStatus,
    syncedRestaurantUUIDs,
  } = useOfflineDataStatus();

  const groupMembersMap = useMemo((): Map<string, Set<string>> => {
    const restaurantGroups = data?.restaurant_groups ?? [];
    const map = new Map<string, Set<string>>();
    for (const group of restaurantGroups) {
      map.set(
        group.uuid,
        new Set(
          (group.restaurant_group_members ?? []).map(
            (member) => member.restaurant_uuid,
          ),
        ),
      );
    }
    return map;
  }, [data?.restaurant_groups]);

  const getStatusFilterOptions = useCallback(
    (statusCounts: Record<ChatStatus, number>): Option[] => {
      return Object.entries(statusCounts).reduce<Option[]>(
        (acc, [status, count]) => {
          if (status === ChatStatus.INACTIVE) {
            return acc;
          }
          const title =
            STATUS_TITLE_MAP[status as keyof typeof STATUS_TITLE_MAP];
          if (count > 0 && title) {
            acc.push({
              subtitle:
                count === 1
                  ? i18n.t('accounts_count.singular')
                  : i18n.t('accounts_count.plural', { count }),
              title,
              value: status,
            });
          }
          return acc;
        },
        [],
      );
    },
    [],
  );

  const getCustomerGroupsFilterOptions = useCallback(
    (eligibleUUIDsByStatusAndOffline: Set<string>): Option[] => {
      const options: Option[] = [];
      const restaurantGroups = data?.restaurant_groups ?? [];
      for (const group of restaurantGroups) {
        const membersUUIDs =
          groupMembersMap.get(group.uuid) ?? new Set<string>();
        let count = 0;
        for (const memberUUID of membersUUIDs) {
          if (eligibleUUIDsByStatusAndOffline.has(memberUUID)) {
            count++;
          }
        }
        if (count > 0) {
          options.push({
            subtitle:
              count === 1
                ? i18n.t('accounts_count.singular')
                : i18n.t('accounts_count.plural', { count }),
            title: group.name,
            value: group.uuid,
          });
        }
      }
      return options;
    },
    [data?.restaurant_groups, groupMembersMap],
  );

  const getSupplierFilterOptions = useCallback(
    (supplierCounts: Record<string, number>): Option[] => {
      return Object.entries(supplierCounts)
        .reduce<Option[]>((acc, [supplierUUID, count]) => {
          const title = supplierUUIDToNameMap.get(supplierUUID);
          if (count > 0 && title) {
            acc.push({
              subtitle:
                count === 1
                  ? i18n.t('accounts_count.singular')
                  : i18n.t('accounts_count.plural', { count }),
              title,
              value: supplierUUID,
            });
          }
          return acc;
        }, [])
        .sort((a, b) => a.title.localeCompare(b.title));
    },
    [supplierUUIDToNameMap],
  );

  const {
    availableOfflineCount,
    filteredData,
    statusFilterOptions,
    customerGroupsFilterOptions,
    supplierFilterOptions,
  } = useMemo(() => {
    const selectedCustomerGroupRestaurantUUIDs =
      selectedCustomerGroupsFilter && selectedCustomerGroupsFilter.size > 0
        ? [...selectedCustomerGroupsFilter].reduce<Set<string>>(
            (acc, groupUUID) => {
              const membersUUIDs = groupMembersMap.get(groupUUID);
              if (membersUUIDs) {
                for (const membersUUID of membersUUIDs) {
                  acc.add(membersUUID);
                }
              }
              return acc;
            },
            new Set(),
          )
        : null;

    const statusCounts: Record<ChatStatus, number> = {
      [ChatStatus.ACTIVE]: 0,
      [ChatStatus.INACTIVE]: 0,
      [ChatStatus.NEW]: 0,
      [ChatStatus.PROSPECT]: 0,
    };
    const filteredData: EmployeeRestaurant[] = [];
    const eligibleUUIDsByStatusAndOffline = new Set<string>();
    const supplierCounts: Record<string, number> = {};
    let availableOfflineCount = 0;

    for (const restaurant of employeeRestaurants) {
      const isAvailableOffline = Boolean(dataStatus?.[restaurant.uuid]);

      const passesOffline =
        accountModeFilter !== SingleSelectFilterType.AvailableOffline ||
        isAvailableOffline;

      const passesStatus =
        selectedStatusFilter.size === 0 ||
        selectedStatusFilter.has(restaurant.chatStatus);

      const passesGroups =
        !selectedCustomerGroupRestaurantUUIDs ||
        selectedCustomerGroupRestaurantUUIDs.has(restaurant.uuid);

      const passesSupplier =
        selectedSupplierFilter.size === 0 ||
        selectedSupplierFilter.has(restaurant.supplierUUID);

      // update the counts (each filter's options reflect the others)
      if (
        passesStatus &&
        passesGroups &&
        passesSupplier &&
        isAvailableOffline
      ) {
        availableOfflineCount++;
      }
      if (passesOffline && passesGroups && passesSupplier) {
        statusCounts[restaurant.chatStatus]++;
      }
      if (passesOffline && passesStatus && passesSupplier) {
        eligibleUUIDsByStatusAndOffline.add(restaurant.uuid);
      }
      if (passesOffline && passesStatus && passesGroups) {
        supplierCounts[restaurant.supplierUUID] =
          (supplierCounts[restaurant.supplierUUID] ?? 0) + 1;
      }

      // the final filter
      if (passesOffline && passesStatus && passesGroups && passesSupplier) {
        filteredData.push(restaurant);
      }
    }

    return {
      availableOfflineCount,
      customerGroupsFilterOptions: getCustomerGroupsFilterOptions(
        eligibleUUIDsByStatusAndOffline,
      ),
      filteredData,
      statusFilterOptions: getStatusFilterOptions(statusCounts),
      supplierFilterOptions: getSupplierFilterOptions(supplierCounts),
    };
  }, [
    accountModeFilter,
    dataStatus,
    employeeRestaurants,
    getCustomerGroupsFilterOptions,
    getStatusFilterOptions,
    getSupplierFilterOptions,
    groupMembersMap,
    selectedCustomerGroupsFilter,
    selectedStatusFilter,
    selectedSupplierFilter,
  ]);

  useEffect(() => {
    if (
      availableOfflineCount === 0 &&
      accountModeFilter === SingleSelectFilterType.AvailableOffline
    ) {
      setAccountModeFilter(null);
    }
  }, [accountModeFilter, availableOfflineCount]);

  const multiSelectFilters = useMemo(() => {
    if (!isSupplierEmployee) {
      return [];
    }
    const filters = [
      {
        isSelecting: expandedMultiSelectFilter === MultiSelectFilterType.Status,
        key: MultiSelectFilterType.Status,
        onClose: () => setExpandedMultiSelectFilter(null),
        onPress: () =>
          setExpandedMultiSelectFilter(MultiSelectFilterType.Status),
        options: statusFilterOptions,
        selected: selectedStatusFilter,
        setSelected: setSelectedStatusFilter,
        title: i18n.t('status'),
      },
    ];

    if (customerGroupsEnabled) {
      filters.push({
        isSelecting:
          expandedMultiSelectFilter === MultiSelectFilterType.CustomerGroups,
        key: MultiSelectFilterType.CustomerGroups,
        onClose: () => setExpandedMultiSelectFilter(null),
        onPress: () =>
          setExpandedMultiSelectFilter(MultiSelectFilterType.CustomerGroups),
        options: customerGroupsFilterOptions,
        selected: selectedCustomerGroupsFilter,
        setSelected: setSelectedCustomerGroupsFilter,
        title: i18n.t('groups'),
      });
    }

    if (showSupplierFilter) {
      filters.unshift({
        isSelecting:
          expandedMultiSelectFilter === MultiSelectFilterType.Supplier,
        key: MultiSelectFilterType.Supplier,
        onClose: () => setExpandedMultiSelectFilter(null),
        onPress: () =>
          setExpandedMultiSelectFilter(MultiSelectFilterType.Supplier),
        options: supplierFilterOptions,
        selected: selectedSupplierFilter,
        setSelected: setSelectedSupplierFilter,
        title: i18n.t('site'),
      });
    }

    return filters;
  }, [
    customerGroupsEnabled,
    customerGroupsFilterOptions,
    expandedMultiSelectFilter,
    isSupplierEmployee,
    selectedCustomerGroupsFilter,
    selectedStatusFilter,
    selectedSupplierFilter,
    showSupplierFilter,
    statusFilterOptions,
    supplierFilterOptions,
  ]);

  const pillButtonProps = useMemo<PillButtonType[]>(() => {
    const pillProps = [];
    if (isSupplierEmployee) {
      if (showSupplierFilter) {
        pillProps.push({
          active: selectedSupplierFilter.size > 0,
          collapsed: supplierFilterOptions.length === 0,
          dropDown: true,
          label: i18n.t('site'),
          onClear: () => setSelectedSupplierFilter(new Set()),
          onPress: () =>
            setExpandedMultiSelectFilter(MultiSelectFilterType.Supplier),
          ref: getPillRef(MultiSelectFilterType.Supplier),
        });
      }

      pillProps.push({
        active: selectedStatusFilter.size > 0,
        collapsed: statusFilterOptions.length === 0,
        dropDown: true,
        label: i18n.t('status'),
        onClear: () => setSelectedStatusFilter(new Set()),
        onPress: () =>
          setExpandedMultiSelectFilter(MultiSelectFilterType.Status),
        ref: getPillRef(MultiSelectFilterType.Status),
      });

      if (customerGroupsEnabled) {
        pillProps.push({
          active: selectedCustomerGroupsFilter.size > 0,
          collapsed: customerGroupsFilterOptions.length === 0,
          dropDown: true,
          label: i18n.t('groups'),
          onClear: () => setSelectedCustomerGroupsFilter(new Set()),
          onPress: () =>
            setExpandedMultiSelectFilter(MultiSelectFilterType.CustomerGroups),
          ref: getPillRef(MultiSelectFilterType.CustomerGroups),
        });
      }
    }
    pillProps.push({
      active: accountModeFilter === SingleSelectFilterType.AvailableOffline,
      collapsed: !isOfflineModeEnabled || availableOfflineCount === 0,
      label: i18n.t('available_offline'),
      onClear: () => setAccountModeFilter(null),
      onPress: () =>
        accountModeFilter === SingleSelectFilterType.AvailableOffline
          ? setAccountModeFilter(null)
          : setAccountModeFilter(SingleSelectFilterType.AvailableOffline),
    });
    return pillProps;
  }, [
    accountModeFilter,
    availableOfflineCount,
    customerGroupsEnabled,
    customerGroupsFilterOptions.length,
    getPillRef,
    isOfflineModeEnabled,
    isSupplierEmployee,
    selectedCustomerGroupsFilter.size,
    selectedStatusFilter.size,
    selectedSupplierFilter.size,
    showSupplierFilter,
    statusFilterOptions.length,
    supplierFilterOptions.length,
  ]);

  return useMemo(
    () => ({
      accountModeFilter,
      dataStatus,
      getPillRef,
      hasMultipleSuppliers,
      multiSelectFilters,
      pillButtonProps,
      refetchDataStatus,
      restaurantRelationships: filteredData,
      syncedRestaurantUUIDs,
    }),
    [
      accountModeFilter,
      dataStatus,
      getPillRef,
      hasMultipleSuppliers,
      filteredData,
      multiSelectFilters,
      pillButtonProps,
      refetchDataStatus,
      syncedRestaurantUUIDs,
    ],
  );
};
